import React from 'react';
import { compose } from 'react-apollo';
import { WrappedComponentProps, injectIntl } from 'react-intl';
import { get } from 'lodash';
import { connect } from 'react-redux';
import { Button, Tooltip, ThemeStyle } from '@treelab/panda';

import SwitchView from './SwitchView';

import { IDispatch, IRootState } from '../../../modules/stores';
import { GLYPHS } from '../../../constants/icons';

import { ActionContainer, Container, RecordBtnMixin } from './styled';

type IConnectProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;
interface IProps extends IConnectProps, WrappedComponentProps {
  openRecordListModal: () => void;
  openSettingsModal: () => void;
  openPrintSettingsModal: () => void;
  isSmallWindow: boolean;
  isTinyWindow: boolean;
}

const ExpandControls: React.FC<IProps> = ({
  recordAllIds,
  currentRecordId,
  changeRecord,
  saveSpaceConfigsByTemplate,
  toggleEditingMode,
  unfoldShadowClones,
  isEditingMode,
  isViewEmpty,
  canEdit,
  openRecordListModal,
  openSettingsModal,
  openPrintSettingsModal,
  isSmallWindow,
  isTinyWindow,
  intl: { formatMessage },
}) => {
  const currentIdIdx = recordAllIds?.findIndex((id: string) => id === currentRecordId) || 0;

  return (
    <Container>
      <ActionContainer>
        <SwitchView />
        <Tooltip
          contentTitle={formatMessage({ id: 'controls.prev' })}
          placement="bottom"
          isDisabled={isViewEmpty || !isSmallWindow}
        >
          <Button
            css={RecordBtnMixin}
            buttonType="text"
            onClick={() => changeRecord(-1)}
            colorTheme={ThemeStyle.black}
            icon={GLYPHS.ARROW_LEFT}
            iconSize={{ height: 15 }}
            isDisabled={isViewEmpty || currentIdIdx === 0}
          >
            {!isSmallWindow && formatMessage({ id: 'controls.prev' })}
          </Button>
        </Tooltip>

        <Tooltip
          contentTitle={formatMessage({ id: 'controls.next' })}
          placement="bottom"
          isDisabled={isViewEmpty || !isSmallWindow}
        >
          <Button
            css={RecordBtnMixin}
            buttonType="text"
            onClick={() => changeRecord(1)}
            colorTheme={ThemeStyle.black}
            trailingIcon={GLYPHS.ARROW_RIGHT}
            trailingIconSize={{ height: 15 }}
            isDisabled={isViewEmpty || currentIdIdx === recordAllIds?.length - 1}
          >
            {!isSmallWindow && formatMessage({ id: 'controls.next' })}
          </Button>
        </Tooltip>

        <Tooltip
          contentTitle={
            isSmallWindow ? formatMessage({ id: 'controls.search' }) : isMac ? '⌘ + F' : 'Ctrl + F'
          }
          contentDesc={isSmallWindow ? (isMac ? '⌘ + F' : 'Ctrl + F') : undefined}
          placement="bottom"
          isDisabled={isViewEmpty}
        >
          <Button
            css={RecordBtnMixin}
            buttonType="text"
            onClick={openRecordListModal}
            colorTheme={ThemeStyle.black}
            icon={GLYPHS.SEARCH}
            iconSize={{ height: 15 }}
            isDisabled={isViewEmpty}
          >
            {!isSmallWindow && formatMessage({ id: 'controls.search' })}
          </Button>
        </Tooltip>
      </ActionContainer>

      {(!isTinyWindow || isEditingMode) && (
        <ActionContainer>
          {canEdit && (
            <Tooltip
              contentTitle={
                isEditingMode
                  ? formatMessage({ id: 'controls.edit.done' })
                  : formatMessage({ id: 'controls.edit.start' })
              }
              contentDesc={formatMessage({ id: 'controls.edit.tooltip' })}
              style={{ width: '180px' }}
              placement="bottom"
              isDisabled={!isEditingMode}
            >
              <Button
                css={RecordBtnMixin}
                buttonType="text"
                onClick={() => {
                  // Auto save if exiting editing mode.
                  if (isEditingMode) {
                    saveSpaceConfigsByTemplate();
                    unfoldShadowClones();
                  }
                  toggleEditingMode();
                }}
                colorTheme={ThemeStyle.blue}
                icon={GLYPHS.EDIT}
                iconSize={{ height: 15 }}
              >
                {!isSmallWindow
                  ? isEditingMode
                    ? formatMessage({ id: 'controls.edit.done' })
                    : formatMessage({ id: 'controls.edit.start' })
                  : undefined}
              </Button>
            </Tooltip>
          )}

          {!isEditingMode && (
            <Tooltip
              contentTitle={
                isSmallWindow
                  ? formatMessage({ id: 'controls.print' })
                  : isMac
                  ? '⌘ + P'
                  : 'Ctrl + P'
              }
              contentDesc={isSmallWindow ? (isMac ? '⌘ + P' : 'Ctrl + P') : undefined}
              placement="bottom"
            >
              <Button
                buttonType="text"
                onClick={openPrintSettingsModal}
                css={RecordBtnMixin}
                colorTheme={ThemeStyle.black}
                icon={GLYPHS.PRINTER}
                iconSize={{ height: 16 }}
              >
                {!isSmallWindow && formatMessage({ id: 'controls.print' })}
              </Button>
            </Tooltip>
          )}

          {canEdit && (
            <Tooltip
              contentTitle={formatMessage({ id: 'controls.settings' })}
              placement="bottom"
              isDisabled={isViewEmpty || !isSmallWindow}
            >
              <Button
                buttonType="text"
                css={RecordBtnMixin}
                onClick={() => openSettingsModal()}
                colorTheme={ThemeStyle.black}
                icon={GLYPHS.SETTINGS}
                iconSize={{ height: 16 }}
              >
                {!isSmallWindow && formatMessage({ id: 'controls.settings' })}
              </Button>
            </Tooltip>
          )}
        </ActionContainer>
      )}
    </Container>
  );
};

const isMac = () => navigator.platform.toUpperCase().indexOf('MAC') >= 0;

const mapState = ({ core, space }: IRootState) => ({
  recordAllIds: get(core, `viewById[${core.currentViewId}].recordsAllIds`),
  isEditingMode: space.isEditingMode,
  currentRecordId: core.currentRecordId,
  isViewEmpty: core.isViewEmpty,
  canEdit: core.canEdit,
});
const mapDispatch = ({ space, core }: IDispatch) => ({
  saveSpaceConfigsByTemplate: space.saveSpaceConfigsByTemplate,
  toggleEditingMode: space.toggleEditingMode,
  unfoldShadowClones: space.unfoldShadowClones,
  changeRecord: core.changeRecord,
});

export default compose(
  injectIntl,
  connect(
    mapState,
    // @ts-ignore
    mapDispatch,
  ),
)(ExpandControls);
